// React core hooks

// Date picker and related locale imports
import AirDatepicker, {
  type AirDatepickerLocale,
  type AirDatepickerOptions,
  type AirDatepickerPosition,
} from 'air-datepicker';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// Internationalization
import { useTranslation } from 'react-i18next';
import type { CellComponent, RowComponent, Tabulator } from 'tabulator-tables';
// Custom hooks (business logic and data management)
import useBoxManagement from '@/domains/asset-management/hooks/useBoxManagement';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
// UI Components
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import GeneralInput from '@/shared/components/ui/GeneralInput/GeneralInput';
import { ReactTabulator } from '@/shared/components/ui/LocalizedTabulator/LocalizedTabulator';
import LogList from '@/shared/components/ui/LogList/LogList';
import Select from '@/shared/components/ui/Select/Select';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import { remoteSortHandler, useServerTable } from '@/shared/hooks/useServerTable';
import useSiteManagement from '@/shared/hooks/useSiteManagement';
import { formatFullDateTimeDDMMYYYY } from '@/shared/utils/date';
import 'air-datepicker/air-datepicker.css';
import localeEn from 'air-datepicker/locale/en';
import localeId from 'air-datepicker/locale/id';
import localeKo from 'air-datepicker/locale/ko';
import dayjs from 'dayjs';
// Types
import type {
  BoxDetailData,
  BoxFormValues,
  ButtonState,
  CreateBoxPayload,
  SelectedBoxType,
  UpdateBoxPayload,
} from '@/domains/asset-management/types/box';
// Plugins and utilities
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

const commonColumnProps = {
  hozAlign: 'center' as const,
  headerHozAlign: 'center' as const,
  resizable: false,
};

// Columns whose `field` matches the backend allowed-sort whitelist keys.
// box endpoint allows: id (remote_terminal_unit_id) | updated_time.
const BOX_SORT_FIELD_MAP: Record<string, string> = {
  remote_terminal_unit_id: 'id',
  updated_time: 'updated_time',
};

const BoxManagement = () => {
  const { t, i18n } = useTranslation();

  const tbRef = useRef<Tabulator | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const [disabledForm, setDisabledForm] = useState<boolean>(true);
  const [disabledId] = useState<boolean>(true);
  const [hasChangesUpdate, setHasChangesUpdate] = useState<boolean>(false);
  const [hasChangesCreate, setHasChangesCreate] = useState<boolean>(false);
  const [isNewClicked, setIsNewClicked] = useState<boolean>(false);
  const hasChangesUpdateRef = useRef(hasChangesUpdate);
  const hasChangesCreateRef = useRef(hasChangesCreate);
  const isNewClickedRef = useRef(isNewClicked);
  const [newId, setNewId] = useState<number | null>(null);

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  //Params
  const [selectedOption, setSelectedOption] = useState<string>('');
  const table = useServerTable<{ input?: string; site_type?: string }>({
    defaultSort: { field: 'updated_time', dir: 'desc' },
    defaultSize: 10,
  });
  const queryParams = table.queryString;

  // Columns are built in-component so the "No" formatter can use the server
  // page/size (Tabulator's own pagination is disabled for remote paging).
  const boxColumns = useMemo(
    () => [
      {
        title: 'No',
        formatter: (cell: CellComponent) =>
          (table.page - 1) * table.size + cell.getRow().getPosition(true),
        width: 60,
        headerSort: false,
        ...commonColumnProps,
      },
      // TODO: i18n - hardcoded Korean string
      {
        title: '함체 ID',
        field: 'remote_terminal_unit_id',
        widthGrow: 0.6,
        headerSort: true,
        ...commonColumnProps,
      },
      {
        title: '명칭',
        field: 'name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: '시리얼 넘버',
        field: 's_n',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: '매핑 사이트 타입',
        field: 'site_type_value',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: '매핑 사이트',
        field: 'site_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: '업데이트 일시',
        field: 'updated_time',
        widthGrow: 0.8,
        headerSort: true,
        ...commonColumnProps,
      },
    ],
    [table.page, table.size]
  );
  const [optionParams] = useState('upper-code=102');
  const [selectedBox, setSelectedBox] = useState<SelectedBoxType>({
    rtu_id: null,
  });
  const selectedBoxRef = useRef<SelectedBoxType>(selectedBox);

  const detailBox = useCallback((data: BoxDetailData) => {
    return {
      remote_terminal_unit_id: data.remote_terminal_unit_id || null,
      site_id: data.site_id || 'NO_MAPPING',
      name: data.name || null,
      description: data.description || null,
      installed_date: data.installed_date || null,
      pic_name: data.pic_name || null,
      pic_phone_number: data.pic_phone_number || null,
      model: data.model || null,
      s_n: data.s_n || null,
      lat: data.lat || null,
      lng: data.lng || null,
      manufacturer: data.manufacturer || null,
      edge1_ip: data.edge1_ip || null,
      edge2_ip: data.edge2_ip || null,
      kafka_url: data.kafka_url || null,
      mqtt_url: data.mqtt_url || null,
      router_ip: data.router_ip || null,
      switch_ip: data.switch_ip || null,
      network_monitoring_url: data.network_monitoring_url || null,
      network_management_url: data.network_management_url || null,
      env_board_ip: data.env_board_ip || null,
      env_board_port: data.env_board_port || null,
      env_board_mac: data.env_board_mac || null,
      config_json: data.config_json || null,
    };
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormValues((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));

      if (isNewClickedRef.current) {
        setHasChangesCreate(true);
      } else {
        setHasChangesUpdate(true);
      }

      if (selectedBoxRef.current?.rtu_id) {
        setHasChangesUpdate(true);
      }
    },
    []
  );

  useEffect(() => {
    let locale: AirDatepickerLocale;
    if (i18n.language === 'eng') {
      locale = localeEn;
    } else if (i18n.language === 'ind') {
      locale = localeId;
    } else {
      locale = localeKo;
    }

    const optionsDate: AirDatepickerOptions<HTMLElement> = {
      autoClose: true,
      locale: locale,
      position: 'top center' as AirDatepickerPosition,

      // Handle single/multiple Airdatepicker output: formatted Data can be string or string[]
      onSelect: (date) => {
        const selected = Array.isArray(date.formattedDate)
          ? date.formattedDate[0]
          : date.formattedDate;
        handleInputChange({
          target: {
            name: 'installed_date',
            value: dayjs(selected).format('YYYY-MM-DD'),
          },
        } as unknown as React.ChangeEvent<HTMLInputElement>);
        setFormValues((prevValues) => ({
          ...prevValues,
          installed_date: dayjs(selected).format('YYYY-MM-DD'),
        }));
      },
    };

    const datepicker = new AirDatepicker('[name="installed_date"]', optionsDate);

    return () => {
      datepicker.destroy();
    };
  }, [i18n.language, handleInputChange]);

  useEffect(() => {
    hasChangesUpdateRef.current = hasChangesUpdate;
    hasChangesCreateRef.current = hasChangesCreate;
    isNewClickedRef.current = isNewClicked;
    selectedBoxRef.current = selectedBox;
  }, [hasChangesUpdate, hasChangesCreate, isNewClicked, selectedBox]);

  //Values
  const [formValues, setFormValues] = useState<BoxFormValues>({
    remote_terminal_unit_id: null,
    site_id: null,
    name: null,
    description: null,
    installed_date: null,
    pic_name: null,
    pic_phone_number: null,
    model: null,
    s_n: null,
    lat: null,
    lng: null,
    manufacturer: null,
    edge1_ip: null,
    edge2_ip: null,
    kafka_url: null,
    mqtt_url: null,
    router_ip: null,
    switch_ip: null,
    network_monitoring_url: null,
    network_management_url: null,
    env_board_ip: null,
    env_board_port: null,
    env_board_mac: null,
    config_json: null,
  });
  const emptyDetail = () => {
    setFormValues((prevValues) => {
      const emptyValues = {} as BoxFormValues;

      (Object.keys(prevValues) as (keyof BoxFormValues)[]).forEach((key) => {
        emptyValues[key] = null;
      });
      return emptyValues;
    });
  };

  //Button
  const [buttonState, setButtonState] = useState<ButtonState>({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
  });

  const disableAllButtons = useCallback(() => {
    setButtonState({
      confirm: true,
      cancel: true,
      delete: true,
      create: true,
    });
  }, []);

  const enableInitialButtons = () => {
    disableAllButtons();
    setIsNewClicked(false);
    setButtonState((prevState) => ({
      ...prevState,
      create: false,
    }));
  };

  const enableUPDATEButtons = useCallback(() => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
      delete: false,
      create: false,
    }));
  }, [disableAllButtons]);

  const enableRegisterButtons = () => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
    }));
  };

  const reloadCallback = () => {
    enableInitialButtons();
    tbRef.current?.deselectRow();
    emptyDetail();
    setDisabledForm(true);
    setHasChangesUpdate(false);
    setIsNewClicked(false);
    setHasChangesCreate(false);
    setSelectedBox({ rtu_id: null });
    setNewId(null);
  };

  const { boxListData, detailBoxData, createBox, deleteBox, updateBox, refetchDetailBox } =
    useBoxManagement({
      rtuID: selectedBox?.rtu_id,
      queryParams: queryParams,
      onDeleteSuccess: reloadCallback,
      onUpdateSuccess: () => {
        refetchDetailBox();
        enableUPDATEButtons();
        setIsNewClicked(false);
        setHasChangesUpdate(false);
      },
      onCreateSuccess: (responseData) => {
        reloadCallback();
        setNewId(responseData);
      },
    });

  const { siteListData } = useSiteManagement({ siteId: null });
  const pageData = boxListData?.data;
  const data = pageData?.rows ?? [];
  const siteData = siteListData?.data;
  const dataBox = detailBoxData?.data[0];

  const { commonListData } = useCommonCodes({ optionParams });

  const optionsForMappingSite = useMemo(() => {
    if (!commonListData?.['102']) return [];

    return [
      { value: '', label: t('cmn > all') },
      ...(commonListData['102']?.code?.map((code, index) => ({
        value: String(code),
        label: commonListData['102']?.name[index] ?? '',
      })) || []),
    ];
  }, [commonListData, t]);

  const handleOnChangeInputSelect = useCallback(({ target }: { target: { value: string } }) => {
    const { value } = target;
    setSelectedOption(value);
  }, []);

  const languageTabulator = () => {
    const datalanguage = {
      pagination: {
        first: t('cmn > first page'),
        first_title: t('cmn > first page'),
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
    return datalanguage;
  };

  const optionsTabulator = {
    debugInvalidOptions: true,
    // Server-side pagination: Tabulator's own pager is off; <TablePagination> drives it.
    pagination: false,
    rowHeight: 41,
    selectableRows: false,
    movableRows: false,
    index: 'remote_terminal_unit_id',
    resizafbleRows: false,
    locale: 'ko',
    langs: {
      ko: languageTabulator(),
    },
    selectableRowsCheck: (row: RowComponent) => {
      return !row.getElement().classList.contains('tabulator-selected');
    },
    placeholder: "<div style='text-align:center;'> No Data</div>",
  };

  const handleSearch = useCallback(
    (inputVal: string) => {
      table.setFilters({
        input: inputVal || undefined,
        site_type: selectedOption || undefined,
      });
    },
    [selectedOption, table]
  );

  const handleReset = () => {
    setSelectedOption('');
    table.reset();
  };

  const handleRowSelected = useCallback(
    (row: RowComponent) => {
      if (hasChangesCreateRef.current || hasChangesUpdateRef.current) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });

        message
          .confirmClicked()
          .then(() => {
            tbRef.current?.deselectRow();
            row.select();
            row.getElement().classList.add('custom-hover');

            const rowData = row.getData();
            setSelectedBox({
              rtu_id: rowData.remote_terminal_unit_id,
            });
            setDisabledForm(false);
            setHasChangesUpdate(false);
            setHasChangesCreate(false);
            setIsNewClicked(false);
          })
          .catch(() => {
            row.deselect();

            const prevId = selectedBoxRef.current.rtu_id;
            if (prevId) {
              const prevRow = tbRef.current?.getRow(prevId) || null;
              prevRow?.select();
              prevRow?.getElement().classList.add('custom-hover');
            }
          });
      } else {
        tbRef.current?.deselectRow();
        row.select();
        row.getElement().classList.add('custom-hover');

        const rowData = row.getData();
        setSelectedBox({
          rtu_id: rowData.remote_terminal_unit_id,
        });
        setDisabledForm(false);
        enableUPDATEButtons();
        setIsNewClicked(false);
      }

      requestAnimationFrame(() => {
        if (nameRef.current) {
          nameRef.current.focus();
        }
      });
    },
    [enableUPDATEButtons, t]
  );

  const handleNewButtonClick = () => {
    if (hasChangesCreate || hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setIsNewClicked(true);
        enableRegisterButtons();
        setSelectedBox({ rtu_id: null });
        setDisabledForm(false);
        setHasChangesUpdate(false);
      });
    } else {
      tbRef.current?.deselectRow();
      emptyDetail();
      setIsNewClicked(true);
      enableRegisterButtons();
      setSelectedBox({ rtu_id: null });
      setDisabledForm(false);
      setHasChangesUpdate(false);
    }
    requestAnimationFrame(() => {
      if (nameRef.current) {
        nameRef.current.focus();
      }
    });
  };

  const handleCancelButtonClick = () => {
    if (hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setDisabledForm(true);
        enableInitialButtons();
        setHasChangesUpdate(false);
        setSelectedBox({ rtu_id: null });
      });
    } else if (isNewClicked) {
      if (hasChangesCreate) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });
        message.confirmClicked().then(() => {
          emptyDetail();
          setIsNewClicked(false);
          setDisabledForm(true);
          enableInitialButtons();
          setHasChangesCreate(false);
        });
      } else {
        emptyDetail();
        setIsNewClicked(false);
        setDisabledForm(true);
        enableInitialButtons();
        setHasChangesCreate(false);
      }
    } else {
      reloadCallback();
    }
  };

  const handleRegistButtonClick = () => {
    const {
      remote_terminal_unit_id: _remote_terminal_unit_id,
      site_id,
      lat,
      lng,
      description,
      edge2_ip: _edge2_ip,
      kafka_url: _kafka_url,
      mqtt_url: _mqtt_url,
      network_monitoring_url: _network_monitoring_url,
      network_management_url: _network_management_url,
      config_json,
      ...fieldsToCheck
    } = formValues;

    const isEmptyField = Object.values(fieldsToCheck).some(
      (value) =>
        value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
    );

    if (isEmptyField || lat == null || lng == null) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      // TODO: i18n - hardcoded Korean string
      new NoticeMessage('필수 값을 모두 입력해주세요.');
      return;
    }

    const payload: CreateBoxPayload = {
      site_id: !site_id || site_id === 'NO_MAPPING' ? null : Number(site_id),
      name: formValues.name ?? '',
      description: description || undefined,
      installed_date: formValues.installed_date ?? '',
      pic_name: formValues.pic_name ?? '',
      pic_phone_number: formValues.pic_phone_number ?? '',
      model: formValues.model ?? '',
      s_n: formValues.s_n ?? '',
      lat: lat,
      lng: lng,
      manufacturer: formValues.manufacturer ?? '',
      edge1_ip: formValues.edge1_ip ?? '',
      edge2_ip: formValues.edge2_ip || null,
      kafka_url: formValues.kafka_url || null,
      mqtt_url: formValues.mqtt_url || null,
      router_ip: formValues.router_ip ?? '',
      switch_ip: formValues.switch_ip ?? '',
      network_monitoring_url: formValues.network_monitoring_url || null,
      network_management_url: formValues.network_management_url || null,
      env_board_ip: formValues.env_board_ip ?? '',
      env_board_port: formValues.env_board_port ?? '',
      env_board_mac: formValues.env_board_mac ?? '',
      config_json: config_json,
    };
    createBox(payload);
  };

  const handleConfirmButtonClick = () => {
    const {
      lat,
      lng,
      site_id,
      config_json,
      description,
      edge2_ip,
      kafka_url,
      mqtt_url,
      network_monitoring_url,
      network_management_url,
      remote_terminal_unit_id,
      ...fieldsToCheck
    } = formValues;

    const isEmptyField = Object.values(fieldsToCheck).some(
      (value) =>
        value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
    );

    if (isEmptyField || lat == null || lng == null) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      // TODO: i18n - hardcoded Korean string
      new NoticeMessage('필수 값을 모두 입력해주세요.');
      return;
    }

    // make a payload to ensure type safety, since formValues may contain null
    const payload: UpdateBoxPayload = {
      site_id: !site_id || site_id === 'NO_MAPPING' ? null : Number(formValues.site_id),
      name: formValues.name ?? '',
      description: description || undefined,
      installed_date: formValues.installed_date ?? '',
      pic_name: formValues.pic_name ?? '',
      pic_phone_number: formValues.pic_phone_number ?? '',
      model: formValues.model ?? '',
      s_n: formValues.s_n ?? '',
      lat,
      lng,
      manufacturer: formValues.manufacturer ?? '',
      edge1_ip: formValues.edge1_ip ?? '',
      edge2_ip: edge2_ip || null,
      kafka_url: kafka_url || null,
      mqtt_url: mqtt_url || null,
      router_ip: formValues.router_ip ?? '',
      switch_ip: formValues.switch_ip ?? '',
      network_monitoring_url: network_monitoring_url || null,
      network_management_url: network_management_url || null,
      env_board_ip: formValues.env_board_ip ?? '',
      env_board_port: formValues.env_board_port ?? '',
      env_board_mac: formValues.env_board_mac ?? '',
      config_json: config_json,
      remote_terminal_unit_id: Number(remote_terminal_unit_id),
    };

    updateBox({
      boxData: payload,
      params: {
        remote_terminal_unit_id: Number(payload.remote_terminal_unit_id),
      },
    });
  };

  const handleDeleteButtonClick = () => {
    if (dataBox?.site_id) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      // TODO: i18n - hardcoded Korean string
      new NoticeMessage('해당 함체에 매핑된 사이트가 존재합니다. 먼저 매핑을 해제해주세요');
      return;
    }
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('msg > delete confirm'), {
      mode: 'confirm',
    });
    message.confirmClicked().then(() => {
      deleteBox(selectedBox?.rtu_id ?? '');
      if (refetchDetailBox) {
        refetchDetailBox();
      }
    });
  };

  useEffect(() => {
    if (dataBox) {
      const data = detailBox(dataBox);
      setFormValues(data);
    }
  }, [dataBox, detailBox]);

  const logData = dataBox
    ? [
        { label: t('cmn > registered by'), value: dataBox.registered_by ?? '' },
        {
          label: t('cmn > registered time'),
          value: dataBox.registered_time
            ? formatFullDateTimeDDMMYYYY(new Date(dataBox.registered_time))
            : dataBox.registered_time,
        },
        { label: t('cmn > updated by'), value: dataBox.updated_by ?? '' },
        {
          label: t('cmn > updated time'),
          value: dataBox.updated_time
            ? formatFullDateTimeDDMMYYYY(new Date(dataBox.updated_time))
            : dataBox.updated_time,
        },
      ]
    : [];

  return (
    <section className="wrap">
      <div className="header-title">
        {/* TODO: i18n - hardcoded Korean string */}
        <h3>시설물 관리</h3>
        <h3>&gt;</h3>
        <h3>함체 속성 관리</h3>
      </div>

      <ContainerCard>
        {/* TODO: i18n - hardcoded Korean string */}
        <Filtering
          searchRef={searchRef}
          labelSelect="매핑 사이트 타입"
          placeholder="명칭 / 시리얼 넘버"
          disableFiltering={true}
          onReset={handleReset}
          onSearch={handleSearch}
        >
          <Select
            value={selectedOption}
            onChange={handleOnChangeInputSelect}
            options={optionsForMappingSite}
          />
        </Filtering>
      </ContainerCard>

      <ContainerCard>
        <div className="table-container relative">
          <ReactTabulator
            data={data}
            columns={boxColumns}
            layout={'fitColumns'}
            className="tabulator-custom w-full "
            id="box-management-table"
            options={optionsTabulator}
            onRef={(r) => {
              tbRef.current = r.current;
            }}
            events={{
              rowClick: (_e: UIEvent, row: RowComponent) => handleRowSelected(row),
              // Header click → delegate sorting to the server. Tabulator also re-sorts the
              // current page locally (same key as the server), so it stays consistent.
              // Guard against programmatic re-sorts (which fire on every data load) to
              // avoid resetting the page on background refetches.
              dataSorted: remoteSortHandler(table, BOX_SORT_FIELD_MAP),
              tableBuilt: () => {
                if (!tbRef.current) return;
                if (selectedBox?.rtu_id) {
                  try {
                    const row = tbRef.current.getRow(selectedBox?.rtu_id) || null;
                    if (row && typeof row.select === 'function') {
                      row.select();
                      row.getElement().classList.add('custom-hover');
                    }
                  } catch {
                    /* row not found */
                  }
                } else if (newId) {
                  try {
                    const row = tbRef.current.getRow(newId) || null;
                    if (row && typeof row.select === 'function') {
                      tbRef.current.scrollToRow(row, 'bottom', true);
                      row.select();
                      row.getElement().classList.add('custom-hover');
                      handleRowSelected(row);
                    }
                  } catch {
                    /* row not found */
                  }
                }
              },
            }}
          />

          {/* Server-side Pagination Controls - Tabulator style */}
          <div className="tabulator-footer">
            <div className="tabulator-paginator">
              <button
                type="button"
                className="tabulator-page"
                onClick={() => table.setPage(1)}
                disabled={table.page === 1}
              >
                {t('cmn > first page')}
              </button>
              <button
                type="button"
                className="tabulator-page"
                onClick={() => table.setPage(table.page - 1)}
                disabled={table.page === 1}
              >
                {t('cmn > page before')}
              </button>
              <span className="tabulator-pages">
                {Array.from({ length: Math.min(5, pageData?.total_pages ?? 0) }, (_, i) => {
                  const totalPages = pageData?.total_pages ?? 0;
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (table.page <= 3) {
                    pageNum = i + 1;
                  } else if (table.page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = table.page - 2 + i;
                  }
                  return (
                    <button
                      type="button"
                      key={pageNum}
                      className={`tabulator-page ${table.page === pageNum ? 'active' : ''}`}
                      onClick={() => table.setPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </span>
              <button
                type="button"
                className="tabulator-page"
                onClick={() => table.setPage(table.page + 1)}
                disabled={table.page === (pageData?.total_pages ?? 0)}
              >
                {t('cmn > next page')}
              </button>
              <button
                type="button"
                className="tabulator-page"
                onClick={() => table.setPage(pageData?.total_pages ?? 1)}
                disabled={table.page === (pageData?.total_pages ?? 0)}
              >
                {t('cmn > last page')}
              </button>
            </div>
            <div style={{ padding: '0 20px 0 0', textAlign: 'right' }}>
              총 {pageData?.total ?? 0} 건
            </div>
          </div>
        </div>
      </ContainerCard>

      <ContainerCard className=" flex flex-col">
        <div className="gap-2.5 flex flex-col">
          {/* TODO: i18n - hardcoded Korean string */}
          <span className="title2bold ">기본 정보</span>
          <hr className="border-t border-gray-300" />
        </div>

        <div className="box-management-col flex flex-col gap-3">
          <div className="grid grid-cols-3 gap-[50px]">
            {/* TODO: i18n - hardcoded Korean string */}
            <DetailForm
              className="items-center!"
              label="함체 ID"
              value={formValues.remote_terminal_unit_id || ''}
              name="remote_terminal_unit_id"
              disabled={disabledId}
              onChange={handleInputChange}
            />

            <DetailForm
              className="items-center!"
              label="명칭"
              formRef={nameRef}
              required={true}
              value={formValues.name || ''}
              name="name"
              disabled={disabledForm}
              onChange={handleInputChange}
              maxLength={100}
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <div className="flex w-full flex-row gap-x-4">
              <DetailForm
                className="items-center!"
                label="설치 위/경도"
                required={true}
                showInput={false}
              >
                <div className="flex w-full flex-row gap-x-2">
                  <GeneralInput
                    customInput="w-full"
                    disabled={disabledForm}
                    value={formValues.lat || ''}
                    name="lat"
                    onChange={handleInputChange}
                    maxLength={100}
                    pattern={'^[0-9.]*$'}
                  />
                  <GeneralInput
                    customInput="w-full"
                    disabled={disabledForm}
                    value={formValues.lng || ''}
                    name="lng"
                    onChange={handleInputChange}
                    maxLength={100}
                    pattern={'^[0-9.]*$'}
                  />
                </div>
              </DetailForm>
            </div>

            <DetailForm
              className="items-center!"
              label="설치 일시"
              required={true}
              value={formValues.installed_date || ''}
              name="installed_date"
              disabled={disabledForm}
              readonly={true}
            />

            <DetailForm
              inputType={'select'}
              className="items-center!"
              label="매핑 사이트"
              value={formValues.site_id || ''}
              name="site_id"
              disabled={disabledForm}
              onChange={handleInputChange}
              optionSelect={
                siteData
                  ? [
                      { value: 'NO_MAPPING', label: '매핑 없음' },
                      ...siteData.map((site) => ({
                        value: String(site.site_id),

                        label: `${site.name} (${site.site_id})`,
                      })),
                    ]
                  : []
              }
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <DetailForm
              className="items-center!"
              label="담당자"
              required={true}
              value={formValues.pic_name || ''}
              name="pic_name"
              maxLength={100}
              disabled={disabledForm}
              onChange={handleInputChange}
            />

            <DetailForm
              className="items-center!"
              label="담당자 전화번호"
              required={true}
              value={formValues.pic_phone_number || ''}
              name="pic_phone_number"
              maxLength={100}
              pattern={'^[0-9]*$'}
              disabled={disabledForm}
              onChange={handleInputChange}
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <DetailForm
              className="items-center!"
              label="모델명"
              required={true}
              value={formValues.model || ''}
              name="model"
              maxLength={100}
              disabled={disabledForm}
              onChange={handleInputChange}
            />

            <DetailForm
              className="items-center!"
              label="시리얼 넘버"
              required={true}
              value={formValues.s_n || ''}
              name="s_n"
              maxLength={100}
              disabled={disabledForm}
              onChange={handleInputChange}
            />

            <DetailForm
              className="items-center!"
              label="제조사"
              required={true}
              maxLength={100}
              value={formValues.manufacturer || ''}
              name="manufacturer"
              disabled={disabledForm}
              onChange={handleInputChange}
            />
          </div>

          <div className="grid grid-cols-1">
            <DetailForm
              className="h-[90px] [&>div:has(textarea)]:h-full [&>div:has(textarea)]:w-full [&_textarea]:h-full [&_textarea]:w-full"
              inputType="textarea"
              label="설명"
              value={formValues.description || ''}
              name="description"
              disabled={disabledForm}
              onChange={handleInputChange}
              maxLength={500}
            />
          </div>

          <div className="grid grid-cols-1">
            <DetailForm
              className="h-[180px] [&>div:has(textarea)]:h-full [&>div:has(textarea)]:w-full [&_textarea]:h-full [&_textarea]:w-full"
              inputType="textarea"
              label="json 속성 (dev)"
              value={formValues.config_json || ''}
              name="config_json"
              disabled={disabledForm}
              onChange={handleInputChange}
              maxLength={10000}
            />
          </div>

          <div className="gap-2.5 flex flex-col">
            {/* TODO: i18n - hardcoded Korean string */}
            <span className="title2bold ">구성 기기 정보</span>
            <hr className="border-t border-gray-300" />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <DetailForm
              className="items-center!"
              label="Edge1 IP"
              required={true}
              value={formValues.edge1_ip || ''}
              name="edge1_ip"
              maxLength={100}
              pattern={'^[0-9.]*$'}
              disabled={disabledForm}
              onChange={handleInputChange}
            />

            <DetailForm
              className="items-center!"
              label="Edge2 IP"
              required={false}
              value={formValues.edge2_ip || ''}
              name="edge2_ip"
              maxLength={100}
              pattern={'^[0-9.]*$'}
              disabled={disabledForm}
              onChange={handleInputChange}
            />

            <DetailForm
              className="items-center!"
              label="Kafka URL"
              required={false}
              value={formValues.kafka_url || ''}
              name="kafka_url"
              disabled={disabledForm}
              onChange={handleInputChange}
            />

            <DetailForm
              className="items-center!"
              label="MQTT URL"
              required={false}
              value={formValues.mqtt_url || ''}
              name="mqtt_url"
              disabled={disabledForm}
              onChange={handleInputChange}
            />

            <DetailForm
              className="items-center!"
              label="Switch IP"
              required={true}
              value={formValues.switch_ip || ''}
              name="switch_ip"
              maxLength={100}
              pattern={'^[0-9.]*$'}
              disabled={disabledForm}
              onChange={handleInputChange}
            />

            <DetailForm
              className="items-center!"
              label="Router IP"
              required={true}
              maxLength={100}
              pattern={'^[0-9.]*$'}
              value={formValues.router_ip || ''}
              name="router_ip"
              disabled={disabledForm}
              onChange={handleInputChange}
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <div className="flex w-full flex-row gap-x-4">
              <DetailForm
                className="items-center!"
                label="Env Board IP / PORT"
                required={true}
                showInput={false}
              >
                <div className="flex w-full flex-row gap-x-2 items-center">
                  <GeneralInput
                    customInput="w-full"
                    value={formValues.env_board_ip || ''}
                    name="env_board_ip"
                    maxLength={100}
                    pattern={'^[0-9.]*$'}
                    disabled={disabledForm}
                    onChange={handleInputChange}
                  />
                  <span>:</span>
                  <GeneralInput
                    customInput="w-full"
                    value={formValues.env_board_port || ''}
                    name="env_board_port"
                    maxLength={100}
                    pattern={'^[0-9]*$'}
                    disabled={disabledForm}
                    onChange={handleInputChange}
                  />
                </div>
              </DetailForm>
            </div>

            <DetailForm
              className="items-center!"
              label="Env Board Mac address"
              required={true}
              value={formValues.env_board_mac || ''}
              name="env_board_mac"
              maxLength={100}
              disabled={disabledForm}
              onChange={handleInputChange}
            />
          </div>
          <hr className="border-t border-gray-300" />
          <div className="flex items-center justify-between gap-4 w-full">
            <div className="flex-1">{disabledForm ? null : <LogList logs={logData} />}</div>
            <div className="flex-none">
              <ButtonGroup
                onClickDelete={handleDeleteButtonClick}
                onClickNew={handleNewButtonClick}
                onClickCancel={handleCancelButtonClick}
                onClickRegist={handleRegistButtonClick}
                onClickConfirm={handleConfirmButtonClick}
                isNewClicked={isNewClicked}
                cancelButtonState={buttonState.cancel}
                confirmButtonState={hasChangesUpdate ? false : buttonState.confirm}
                deleteButtonState={buttonState.delete}
                newButtonState={hasChangesCreate ? false : buttonState.create}
              />
            </div>
          </div>
        </div>
      </ContainerCard>
    </section>
  );
};

export default BoxManagement;
